import { atom } from 'jotai'
import type { SetStateAction } from 'jotai'

export interface Config {
    project_id: string
    destination_id: string
    lot_id: string
    phase_id: string
    calculation_id: string
    condition_id: string
    graph_id: string
};

export const newConfigAtom = atom<Config>({
    project_id: '',
    destination_id: '',
    lot_id: '',
    phase_id: '',
    calculation_id: '',
    condition_id: '',
    graph_id: '',
});

// Generic "field" atom factory
function fieldAtom<K extends keyof Config>(key: K) {
    return atom(
        (get) => get(newConfigAtom)[key],
        (get, set, update: Config[K]) =>
            set(newConfigAtom, { ...get(newConfigAtom), [key]: update }),
    )
};

export const projectIdAtom = fieldAtom('project_id');
export const destinationIdAtom = fieldAtom('destination_id');
export const lotIdAtom = fieldAtom('lot_id');
export const phaseIdAtom = fieldAtom('phase_id');
export const calculationIdAtom = fieldAtom('calculation_id');
export const graphIdAtom = fieldAtom('graph_id');

// Phenomenon & drive — primitive atoms
export const phenomenonIdAtom = atom<string | null>(null);
export const driveIdAtom = atom<string | null>(null);

// conditionIdAtom 
export const conditionIdAtom = atom<
    string | null,
    [SetStateAction<string | null>],
    void
>(
    /* READ ----------------------------------------------------------- */
    (get) => {
        const ph = get(phenomenonIdAtom)
        const dr = get(driveIdAtom)
        return ph && dr ? `${ph}-${dr}` : null
    },

    /* WRITE ---------------------------------------------------------- */
    (get, set, next) => {
        const value =
            typeof next === 'function' ? next(get(conditionIdAtom)) : next

        /* Push into the two source atoms ------------------------------ */
        if (!value) {
            set(phenomenonIdAtom, null)
            set(driveIdAtom, null)
        } else {
            const [ph, dr] = value.split('-')
            set(phenomenonIdAtom, ph || null)
            set(driveIdAtom, dr || null)
        }

        /* Also reflect in the root Config object ---------------------- */
        set(newConfigAtom, {
            ...get(newConfigAtom),
            condition_id: value ?? '',
        })
    },
);

// Create Project
export interface ProjectPayload {
    description: string;
    units: string[];
};

export const projectPayloadAtom = atom<ProjectPayload>({
    description: '',
    units: [],
});

// Calculation
// 達成度設定のフォーム状態。入力途中を表せるよう値は null 許容（送信 body は
// api/submitLtool の AchievementLevelRequestBody に検証付きで変換する）。
// three_sigma / risk_margin のキーは現象グループ ID（BEV: vib/aco/gn、HEV: vib/gn）。
export interface PhenomenonPayload {
    three_sigma: Record<string, number | null>;
    risk_margin: Record<string, number | null>;
    freq: Record<string, { max: number | null; min: number | null }>;
};
export const phenomenonPayloadAtom = atom<PhenomenonPayload>({
    three_sigma: {},
    risk_margin: {},
    freq: {},
});
